/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

/* eslint-disable no-undef */
import React, { useEffect } from 'react';

import { connect } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { StyledEngineProvider } from '@mui/material/styles';
import { RootState } from '../../store';
import * as BeamActions from '../../store/beams/actions';
import * as GlobalActions from '../../store/actions';
import { OverlaysContextsEnum } from '../../store/maps/reducer';
import { MapContext } from '../common/map/BaseMapComponent';
import { GOOGLE_MAP_INSTANCE_KEY } from '../common/map/GoogleMapSingleton';
import appTheme from '../common/theme/appTheme.json';
import FlightPathContainer from './FlightPath/FlightPathContainer';
import { GX_LATEST_VERSION, VIHNA_LATEST } from '../../shared/entities/beam';
import AddTailTagsToMap from './AddTailTagsToMap';
import {getArcLightMapVersion} from "../../utils/BeamUtils";
import { Antennas } from '../../shared/entities/options/antennas';

export interface AircraftFleetMapContainerProps extends StateProps, DispatchProps {}

export const getBeamsRequestPayload = (antennas: Antennas, antennaTypeId: number) => {
  const supportedNetworks = antennas?.[antennaTypeId]?.supportedNetworks || [];
  // If the antenna supports GX and other networks (e.g Honeywell Jetwave), request beams for GX, ArcLight and VIHNA.
  // If the antenna supports only GX, request beams for GX.
  if (supportedNetworks.includes('GX')) {
    return supportedNetworks.length > 1
      ? [GX_LATEST_VERSION, getArcLightMapVersion(antennaTypeId, undefined), VIHNA_LATEST]
      : [GX_LATEST_VERSION];
  }

  return [getArcLightMapVersion(antennaTypeId, undefined), VIHNA_LATEST];
};

/**
 * This class represents the container for any google map components that will be rendered on the map
 * in the fleet view. It is connected to the redux store and will pass any information to the dumb components
 * that will be drawn on the map.
 */
const AircraftFleetMapContainer = (props: AircraftFleetMapContainerProps) => {
  const { antennas, overlaysCurrentContext, requestBeamsByAntennaTypeId, selectedAntennaTypeId, switchOverlaysContext } = props;

  useEffect(() => {
    if (overlaysCurrentContext !== OverlaysContextsEnum.FLEET_VIEW) {
      switchOverlaysContext(OverlaysContextsEnum.FLEET_VIEW);
    }
  }, [overlaysCurrentContext, switchOverlaysContext]);

  useEffect(() => {
    if (selectedAntennaTypeId && overlaysCurrentContext === OverlaysContextsEnum.FLEET_VIEW) {
      requestBeamsByAntennaTypeId(antennas, selectedAntennaTypeId);
    }
  }, [selectedAntennaTypeId, overlaysCurrentContext, requestBeamsByAntennaTypeId]);

  return (
    <MapContext.Consumer>
      {map => (
        <React.Fragment>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={appTheme}>
              <FlightPathContainer map={map} />
            </ThemeProvider>
          </StyledEngineProvider>
          <AddTailTagsToMap map={map} />
        </React.Fragment>
      )}
    </MapContext.Consumer>
  );
};

const mapStateToProps = (state: RootState) => ({
  antennas: state.antennas.antennas,
  selectedAntennaTypeId:
    state.maps.mapContext[GOOGLE_MAP_INSTANCE_KEY]?.overlaysUiState[state.maps.overlaysCurrentContext]
      ?.selectedAntennaTypeId,
  overlaysCurrentContext: state.maps.overlaysCurrentContext,
});
type StateProps = ReturnType<typeof mapStateToProps>;

const mapDispatchToProps = (dispatch: any) => {
  return {
    switchOverlaysContext: (overlaysContextsType: OverlaysContextsEnum) =>
      dispatch(GlobalActions.switchOverlaysContext(overlaysContextsType)),
    requestBeamsByAntennaTypeId: (antennas: Antennas, antennaTypeId: number) => {
      dispatch(BeamActions.requestBeams(...getBeamsRequestPayload(antennas, antennaTypeId)))
    }
  };
};
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

export default connect(mapStateToProps, mapDispatchToProps)(AircraftFleetMapContainer);
